import { useState, useEffect, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';

interface InviteData {
  id: string;
  email: string;
  storeName?: string;
}

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token) {
      validateInvite();
    }
  }, [token]);

  const validateInvite = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: { valid: boolean; invite?: InviteData; error?: string } }>(`/auth/invite/${token}`);
      if (response.data.data.valid && response.data.data.invite) {
        setInviteData(response.data.data.invite);
      } else {
        setError(response.data.data.error || 'This invite link is invalid or has expired.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'This invite link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await apiClient.post<{ success: boolean; data: { token: string; user: any } }>('/auth/invite/accept', {
        token,
        name: name.trim(),
      });

      // Log them in with the returned token
      login(response.data.data.token);

      // Redirect to store dashboard
      navigate('/store/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invitation. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && !inviteData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Invalid Invite</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Welcome!</h1>
          <p className="mt-2 text-gray-600">
            You've been invited to join Discover The Park
          </p>
        </div>

        <div className="card">
          <div className="text-center mb-6">
            <p className="text-gray-600">
              Setting up store: <strong>{inviteData?.storeName || 'Your Store'}</strong>
            </p>
            <p className="text-sm text-gray-500 mt-1">
              for {inviteData?.email}
            </p>
          </div>

          <form onSubmit={handleAcceptInvite} className="space-y-4">
            <div>
              <label className="label">Your Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="John Smith"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="btn-primary w-full"
            >
              {submitting ? 'Creating Account...' : 'Accept Invitation'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
